import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import media from 'styled-media-query';
import styled from 'styled-components';
import { NotificationEntity } from 'src/firebase/db/Notifications';
import { orderBreadInitialEntity } from 'src/firebase/db/OrderBreads';

import { formatDate } from 'src/helpers/UtilityHelper';
import StyleConfig from 'src/constants/StyleConfig';

import Button from 'src/components/atoms/button/Button';
import CheckBox from 'src/components/atoms/form/CheckBox';
dayjs.extend(isSameOrBefore);

type Props = {
  notification: NotificationEntity;
  isSelected: boolean;
  isAdmin?: boolean;
  onEdit: (notificationId: string) => void;
  onDelete: (notificationId: string) => void;
  onSelect: (id: string) => void;
};

const ReservationListItem = (props: Props) => {
  const {
    notification: {
      id,
      is_draft,
      is_published,
      updated_at,
      published_time,
      order_breads,
      shop_name,
    },
    isSelected,
    isAdmin,
    onEdit,
    onDelete,
    onSelect,
  } = props;

  const wrapOnEdit = useCallback(() => {
    onEdit(id);
  }, [id, onEdit]);

  const wrapOnDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  const wrapOnSelect = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const orderBread = useMemo(() => {
    if (!order_breads.length) {
      return orderBreadInitialEntity;
    }
    return order_breads[0];
  }, [order_breads]);

  return (
    <ListItem>
      <div className="reservationListItem">
        <div className="reservationListItem-body">
          <div className="reservationListItem-thumbnail">
            <img src={orderBread.image} alt={orderBread.name} />
          </div>
          <div className="reservationListItem-description">
            <p className="reservationListItem-name">
              <span>{orderBread.name}</span>
              <CheckBox
                children={''}
                isChecked={isSelected}
                onChange={() => wrapOnSelect()}
              />
            </p>
            {shop_name && isAdmin && (
              <>
                <p className="reservationListItem-timestamp">
                  <span>{shop_name}</span>
                </p>
              </>
            )}
            {(is_draft || isAdmin) && (
              <p className="reservationListItem-timestamp">
                <span>下書き保存日時：</span>
                <span>{formatDate(updated_at, 'YYYY年MM月DD日 HH:mm')}</span>
              </p>
            )}
            {!is_draft && published_time && (
              <p className="reservationListItem-timestamp">
                <span>配信{is_published ? '' : '予定'}日時：</span>
                <span>{formatDate(published_time, 'YYYY年MM月DD日 HH:mm')}</span>
              </p>
            )}
          </div>
        </div>
        <div className="reservationListItem-actions">
          <div className="buttons">
            {!is_draft ? (
              <>
                <Button isDisabled={true} clickEvent={() => {}} text={`配信済み`} />
                <Link to="/reservations/history">配信履歴を確認する</Link>
              </>
            ) : (
              <>
                <Button
                  isDisabled={false}
                  buttonColor="green"
                  textAlign="center"
                  hasIcon={true}
                  clickEvent={() => wrapOnEdit()}
                  text={`編集する`}
                />
                <button className="button-delete" onClick={() => wrapOnDelete()}>
                  下書きを削除する
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ListItem>
  );
};

const ListItem = styled.div`
  background-color: #fff;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  margin-bottom: ${StyleConfig.HELPER.M16};
  padding: 16px;
  line-height: ${20 / 16};

  ${media.lessThan('small')`
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}

  .reservationListItem {
    width: 100%;

    .reservationListItem-body {
      display: flex;
      align-items: center;
      margin-bottom: 16px;

      .reservationListItem-thumbnail {
        padding-right: 12px;
        flex-shrink: 2;
        min-width: 100px;

        img {
          width: 84px;
          height: 62px;
          object-fit: cover;
          object-position: 50% 50%;
          border-radius: 8px;
        }
      }

      .reservationListItem-description {
        flex-grow: 2;

        .reservationListItem-name {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 8px;

          label {
            flex-shrink: 2;
            margin-left: 12px;
            input[type='checkbox'] {
              border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER} !important;

              &:after {
                background-image: url(/images/icon/checked_g.svg);
              }
            }
          }
        }

        .reservationListItem-timestamp {
          font-size: 12px;

          span {
            &:last-child {
              font-weight: bold;
            }
          }
        }
      }
    }

    .reservationListItem-actions {
      .buttons {
        text-align: center;

        button {
          display: block;
          width: 100%;

          &.button-delete {
            color: #ff0303;
            display: inline;
            width: auto;
            text-decoration: underline;
            margin-bottom: 0px;
          }
        }

        button {
          margin-bottom: 16px;
          &:disabled {
            cursor: not-allowed;
          }
        }

        a {
          color: ${StyleConfig.COLOR.GREEN};
          &:visited {
            color: ${StyleConfig.COLOR.GREEN};
          }
        }
      }
    }
  }
`;

export default ReservationListItem;
